//* 계좌환불(수기 이체) 요청 모달 — 가상계좌·무통장입금 결제는 PG 자동환불이 불가해
//* 운영사(admin)가 직접 계좌이체한다. 환불계좌를 접수해 admin 이체 대기열에 올린다.
//* target=Customer: 고객 계좌로 운영사가 이체 / SellerAdvance: 판매자가 선환불하고 상환받는 이체.
//* Requested/Rejected 상태에선 재요청이 기존 요청을 덮어쓴다. Done이면 400. 취소된 주문도 접수 가능.
//* 토스 가상계좌 건은 실제 이체가 필요 없다(환불계좌만 있으면 PG 자동환불) — 이 모달은 원래
//* 자동환불이 없던 시절 것이라, 계좌를 아는 담당자는 자동환불 모달로 넘겨 유도한다(onSwitchToAutoRefund).
//* (스펙: docs/shop-order-seller-request-spec.md · 404 안내는 구서버 안전장치)

import { FC, useCallback, useEffect, useState } from 'react';

import { useRecoilState, useRecoilValue } from 'recoil';

import {
  isSellerAdvancePresetShop,
  SELLER_ADVANCE_ACCOUNT_PRESET,
} from '@service/seller/utils/sellerRequest.constants';
import { sellerShopStateSelectorFamily } from '@shared/api/shop/shop.selector';
import {
  requestShopOrderManualRefund,
  ShopOrderManualRefundRequestDto,
} from '@shared/api/shopOrder/shopOrder.controller';
import { shopOrderListForSellerUrl } from '@shared/api/shopOrder/shopOrder.hook';
import {
  ShopOrderManualRefundTarget,
  ShopOrderWithSellerRequests,
} from '@shared/api/shopOrder/shopOrder.interface';
import { sellerRequestStatusLabel } from '@shared/api/shopOrder/shopOrder.utils';
import { Button } from '@shared/components/Button';
import Modal from '@shared/components/Modal';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { PaymentState } from '@shared/types';
import { formatDate } from '@shared/utils/formatDate';
import { Tag } from 'antd';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

interface Props {
  openId: string;
  order: ShopOrderWithSellerRequests;
  //* 목록 페이지 등 카드 밖에서 열었을 때 새로고침 콜백
  onSuccess?: () => void;
  //* 토스 가상계좌 자동환불이 가능한 주문일 때 호출부가 넘긴다 — 안내 배너 + 전환 버튼이 뜬다.
  //* (이 모달을 닫고 자동환불 모달을 여는 일은 호출부 몫)
  onSwitchToAutoRefund?: () => void;
}

const statusOf = (e: unknown): number | undefined =>
  (e as { response?: { status?: number } })?.response?.status;

const requestErrorMessage = (e: unknown): string => {
  switch (statusOf(e)) {
    case 400:
      return '요청할 수 없는 상태입니다. (이미 이체 완료됐거나 미결제/입금대기 주문)';
    case 403:
      return '권한이 없습니다.';
    case 404:
      return '주문을 찾을 수 없거나 아직 서버에 반영되지 않은 기능입니다.';
    case 409:
      return '요청 상태가 방금 바뀌었습니다. 새로고침 후 다시 확인해주세요.';
    default:
      return '계좌환불 요청에 실패했습니다.';
  }
};

const dateString = (date?: Date) =>
  formatDate(date, {
    dateSeparater: '.',
    contains: { hours: true, minutes: true },
  });

const targetLabel: Record<ShopOrderManualRefundTarget, string> = {
  Customer: '고객계좌환불',
  SellerAdvance: '선지급 상환',
};

export const OrderManualRefundRequestModal: FC<Props> =
  function OrderManualRefundRequestModal({
    openId,
    order,
    onSuccess,
    onSwitchToAutoRefund,
  }: Props) {
    const { sellerRole } = useAuth();
    const [open, setOpen] = useRecoilState(simpleOpenAtomFamily(openId));
    const notifiacationInstance = useRecoilValue(notificationInstanceAtom);
    const queryClient = useQueryClient();

    const existing = order.manualRefund;
    const isDone = existing?.status === 'Done';
    const paymentByCash = order.paymentDetail.paymentByCash ?? 0;

    //* 프리셋 노출 조건: shop 이름에 키워드('마리') 포함 시에만.
    //* admin 목록 행은 주문에 populate된 shop 이름을, 카드/seller 컨텍스트는
    //* 현재 라우트(shopId)의 shop 이름을 본다.
    const { shopId } = useParams();
    const routeShopState = useRecoilValue(
      sellerShopStateSelectorFamily(shopId ?? 'undefined'),
    );
    const orderShopName = typeof order.shop === 'object' ? order.shop?.name : undefined;
    const presetVisible = isSellerAdvancePresetShop(
      orderShopName ?? routeShopState.data?.name,
    );
    //* 미결제/입금대기만 거부 — 취소된 주문도 이체는 수동이므로 접수 가능 (백엔드 동일 검증)
    const notPaidYet = [PaymentState.NOT_PAID, PaymentState.WAITING].includes(
      order.paymentDetail.status,
    );
    //* 이미 취소(요청)된 주문 — 백엔드가 상태를 건드리지 않고 도장만 (중복 알림 없음)
    const alreadyCanceled = [
      PaymentState.CANCELED,
      PaymentState.CANCEL_REQUESTED,
    ].includes(order.paymentDetail.status);
    const canSubmit = !isDone && (!notPaidYet || !!existing);

    const [target, setTarget] = useState<ShopOrderManualRefundTarget>('Customer');
    const [bank, setBank] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [holderName, setHolderName] = useState('');
    const [amount, setAmount] = useState('');
    const [requestNote, setRequestNote] = useState('');
    const [submitting, setSubmitting] = useState(false);

    //* 열리는 순간에만 기존 요청으로 프리필(재요청 = 덮어쓰기), 없으면 금액 = 결제 현금액.
    //* existing/paymentByCash를 deps에 넣으면 편집 중 창 포커스 refetch로 order가 갱신될 때
    //* 입력값(계좌·금액)이 서버값으로 되돌아가 사라짐 — 열림 전환에만 의존한다.
    useEffect(() => {
      if (!open) return;
      setTarget(existing?.target ?? 'Customer');
      setBank(existing?.account?.bank ?? '');
      setAccountNumber(existing?.account?.accountNumber ?? '');
      setHolderName(existing?.account?.holderName ?? '');
      setAmount(String(existing?.amount ?? paymentByCash));
      setRequestNote(existing?.requestNote ?? '');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const onCloseModal = useCallback(() => {
      setOpen(false);
      history.back();
    }, [setOpen]);

    //* 자동환불로 전환 — 이 모달을 닫고(히스토리 포함) 호출부가 자동환불 모달을 연다.
    //! history.back()의 popstate는 마운트된 모든 모달(useModalHook)을 닫는다. 그 전에 다음 모달을
    //! 열면 열자마자 닫히므로, popstate가 지나간 뒤(없으면 짧은 타임아웃 뒤)에 연다.
    const onSwitch = useCallback(() => {
      if (!onSwitchToAutoRefund) return;
      setOpen(false);
      let done = false;
      const openNext = () => {
        if (done) return;
        done = true;
        window.removeEventListener('popstate', openNext);
        onSwitchToAutoRefund();
      };
      window.addEventListener('popstate', openNext);
      setTimeout(openNext, 300);
      history.back();
    }, [onSwitchToAutoRefund, setOpen]);

    const applyPreset = () => {
      setTarget('SellerAdvance');
      setBank(SELLER_ADVANCE_ACCOUNT_PRESET.bank);
      setAccountNumber(SELLER_ADVANCE_ACCOUNT_PRESET.accountNumber);
      setHolderName(SELLER_ADVANCE_ACCOUNT_PRESET.holderName);
    };

    const amountNumber = Number(amount || 0);
    const amountMismatch = amountNumber !== paymentByCash;

    const onSubmit = useCallback(async () => {
      if (!bank.trim() || !accountNumber.trim() || !holderName.trim()) {
        notificateError(
          notifiacationInstance,
          '환불계좌(은행·계좌번호·예금주)를 모두 입력해주세요.',
        );
        return;
      }
      if (!Number.isInteger(amountNumber) || amountNumber < 1) {
        notificateError(notifiacationInstance, '금액은 1 이상의 정수여야 합니다.');
        return;
      }
      setSubmitting(true);
      try {
        const dto: ShopOrderManualRefundRequestDto = {
          account: {
            bank: bank.trim(),
            accountNumber: accountNumber.trim(),
            holderName: holderName.trim(),
          },
          target,
          amount: amountNumber,
          requestNote: requestNote.trim() || undefined,
        };
        await requestShopOrderManualRefund(order._id, dto, sellerRole());
        queryClient.invalidateQueries(shopOrderListForSellerUrl(sellerRole()));
        notificateSuccess(
          notifiacationInstance,
          '계좌환불 요청 접수 완료 — 운영사 이체 대기열에 올라갔습니다.',
        );
        onSuccess?.();
        onCloseModal();
      } catch (err) {
        console.error(err);
        notificateError(notifiacationInstance, requestErrorMessage(err));
      } finally {
        setSubmitting(false);
      }
    }, [
      bank,
      accountNumber,
      holderName,
      amountNumber,
      target,
      requestNote,
      order._id,
      sellerRole,
      queryClient,
      notifiacationInstance,
      onSuccess,
      onCloseModal,
    ]);

    return (
      <Modal openId={openId}>
        <div className="space-y-4">
          <div>
            <p className="text-lg font-bold">계좌환불 요청</p>
            <p className="break-keep pt-1 text-sm text-gray-600">
              가상계좌·무통장입금 결제는 자동 환불이 불가해 운영사가 직접
              계좌이체합니다. 환불받을 계좌를 접수하면 운영사 이체 대기열에 올라가고,
              이체 후 완료
              처리됩니다.
            </p>
          </div>

          {/* 토스 가상계좌 — 실제 이체 없이 환불계좌만 있으면 PG 자동환불이 된다. 계좌를 아는
              담당자는 여기서 접수하지 말고 바로 자동환불로 가도록 유도 (이 모달은 자동환불 이전 것) */}
          {onSwitchToAutoRefund && !isDone && (
            <div className="space-y-2 break-keep rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
              <p>
                이 주문은 <b>토스 가상계좌</b> 결제라 운영사 실제 이체가 필요 없습니다. 환불받을
                계좌를 아신다면 <b>취소(자동환불)</b>로 접수하세요 — 토스가 그 계좌로 바로
                환불합니다.
                {existing?.status === 'Requested' &&
                  ' 이미 접수된 요청의 계좌가 자동환불 모달에 채워집니다.'}
              </p>
              <Button
                className="button-rectangle min-h-[36px] w-full border-none bg-warn text-white"
                onClick={onSwitch}
              >
                취소(자동환불)로 진행
              </Button>
              <p className="text-xs text-amber-700">
                계좌를 아직 모르거나 토스가 환불을 거절한 경우에만 아래 계좌환불(수기 이체)
                요청을 이용하세요.
              </p>
            </div>
          )}

          {/* 주문 요약 */}
          <div className="rounded-md border bg-gray-50 p-3 text-sm">
            <p className="font-medium">{order.orderDetail?.shopItemEmbed?.name}</p>
            <p className="text-gray-700">
              결제 {paymentByCash.toLocaleString()}원 · {order.paymentDetail.method}
              <span className="text-xs text-gray-500" title={`주문번호: ${order._id}`}>
                {' '}
                · 주문 {order._id.slice(-6)}
              </span>
            </p>
          </div>

          {/* 기존 요청 상태 */}
          {existing && (
            <div className="rounded-md border p-3 text-sm">
              <p className="flex items-center gap-2">
                <Tag
                  className={
                    existing.status === 'Done'
                      ? 'border-green-600 text-green-700'
                      : existing.status === 'Rejected'
                      ? 'border-red-500 text-red-600'
                      : 'border-amber-500 text-amber-700'
                  }
                >
                  {sellerRequestStatusLabel[existing.status]}
                </Tag>
                <Tag className="border-gray-400 text-gray-600">
                  {targetLabel[existing.target]}
                </Tag>
                <span className="text-gray-600">
                  요청일 {dateString(existing.requestedAt)}
                </span>
              </p>
              {existing.status === 'Done' && (
                <div className="pt-1 text-gray-700">
                  <p>이체 처리일 {dateString(existing.processedAt)}</p>
                  {existing.resultNote && <p>처리 메모: {existing.resultNote}</p>}
                </div>
              )}
              {existing.status === 'Rejected' && (
                <p className="break-keep pt-1 text-red-600">
                  반려 사유: {existing.resultNote || '(사유 미기재)'} — 내용을 수정해
                  다시 요청할 수 있습니다.
                </p>
              )}
              {existing.status === 'Requested' && (
                <p className="break-keep pt-1 text-xs text-gray-500">
                  이체 대기 중입니다. 다시 접수하면 기존 요청 내용을 덮어씁니다.
                </p>
              )}
            </div>
          )}

          {!canSubmit && !existing && (
            <p className="break-keep text-sm text-red-600">
              미결제/입금대기 주문은 환불 요청할 수 없습니다.
            </p>
          )}

          {/* 입력 필드 */}
          {!isDone && (
            <div className="space-y-2 text-sm">
              {/* 이체 대상 */}
              <div className="flex gap-2">
                {(['Customer', 'SellerAdvance'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`flex-1 rounded-md border p-2 text-left ${
                      target === t
                        ? 'border-cyan-600 bg-cyan-50 font-bold'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                    onClick={() => setTarget(t)}
                  >
                    <div className="flex items-center justify-between gap-1">
                      <div className="min-w-0">
                        <p>{targetLabel[t]}</p>
                        <p className="text-xs font-normal text-gray-500">
                          {t === 'Customer' ? '고객계좌로 이체요청' : '선지급 상환 요청'}
                        </p>
                      </div>
                      {/* 고객계좌환불 카드 우측: 계좌 초기화 (프리셋 등으로 채운 계좌를 비우고 고객 계좌 입력) */}
                      {t === 'Customer' && (
                        <span
                          role="button"
                          className="shrink-0 rounded border border-gray-400 bg-white px-1.5 py-0.5 text-center text-[11px] font-medium leading-tight text-gray-600 hover:bg-gray-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            setTarget('Customer');
                            setBank('');
                            setAccountNumber('');
                            setHolderName('');
                          }}
                        >
                          계좌
                          <br />
                          초기화
                        </span>
                      )}
                      {/* 프리셋은 선지급 상환 카드 우측의 작은 버튼 — 누르면 계좌 프리필 + target 자동 전환 */}
                      {t === 'SellerAdvance' && presetVisible && (
                        <span
                          role="button"
                          className="shrink-0 rounded border border-cyan-500 bg-white px-1.5 py-0.5 text-center text-[11px] font-medium leading-tight text-cyan-700 hover:bg-cyan-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            applyPreset();
                          }}
                        >
                          신대표님
                          <br />
                          계좌 입력
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
              {/* 취소 자동 배선 안내 — 백엔드가 요청/처리에 맞춰 주문 결제상태를 움직인다 (스펙 §수기환불↔주문취소) */}
              {alreadyCanceled ? (
                <p className="break-keep text-xs text-gray-500">
                  이미 취소(요청)된 주문 — 이체 기록만 남고 중복 취소알림은 나가지
                  않습니다.
                </p>
              ) : (
                <p className="break-keep text-xs text-amber-600">
                  {target === 'Customer'
                    ? '접수하면 주문이 취소요청 상태로 바뀌고, 운영사 이체 완료 시 취소 확정 + 취소알림이 발송됩니다. 반려되면 원래 상태로 돌아갑니다.'
                    : '접수 즉시 주문이 취소 확정되고 고객에게 취소알림이 발송됩니다. 이미 고객에게 선환불한 경우에만 사용하세요.'}
                </p>
              )}
              <div className="flex items-center gap-2">
                <label className="w-20 shrink-0 font-medium">은행</label>
                <input
                  type="text"
                  className="w-full rounded border border-gray-300 px-2 py-1"
                  placeholder="기업은행"
                  value={bank}
                  onChange={(e) => setBank(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="w-20 shrink-0 font-medium">계좌번호</label>
                <input
                  type="text"
                  className="w-full rounded border border-gray-300 px-2 py-1"
                  placeholder="- 없이 입력해도 됩니다"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="w-20 shrink-0 font-medium">예금주</label>
                <input
                  type="text"
                  className="w-full rounded border border-gray-300 px-2 py-1"
                  value={holderName}
                  onChange={(e) => setHolderName(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="w-20 shrink-0 font-medium">금액</label>
                <input
                  type="number"
                  min={1}
                  className="w-32 rounded border border-gray-300 px-2 py-1 text-right"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
                <span>원</span>
              </div>
              {amountMismatch && (
                <p className="break-keep text-xs text-amber-600">
                  결제 현금액({paymentByCash.toLocaleString()}원)과 다릅니다 —
                  부분환불·차액 등 의도한 금액인지 확인해주세요.
                </p>
              )}
              <div className="flex items-center gap-2">
                <label className="w-20 shrink-0 font-medium">메모</label>
                <input
                  type="text"
                  className="w-full rounded border border-gray-300 px-2 py-1"
                  placeholder="요청 경위 등 (선택)"
                  value={requestNote}
                  onChange={(e) => setRequestNote(e.target.value)}
                />
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              className="button-rectangle min-h-[40px] flex-1 border border-gray-300"
              onClick={onCloseModal}
            >
              닫기
            </Button>
            {!isDone && (
              <Button
                className="button-rectangle min-h-[40px] flex-1 theme-text-4 theme-bg-main"
                disabled={submitting || !canSubmit}
                onClick={onSubmit}
              >
                {submitting
                  ? '접수 중...'
                  : existing
                  ? '다시 요청 (덮어쓰기)'
                  : '요청 접수'}
              </Button>
            )}
          </div>
        </div>
      </Modal>
    );
  };
